import { observer } from 'mobx-react-lite'
import { ExpandableCardListEmptyState } from '@/components/ExpandableCardList'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import type { TaxPrepOrganizerViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/TaxPrepOrganizerViewModel'
import { RentalAccordion } from '@/features/jobs/details/tax-type/tax-prep-organizer/rentals/components/RentalAccordion'
import { Id } from '@/infra/id'
import { RemoveRentalDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/rentals/components/RemoveRentalDialog'
import { StepCardListContainer } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/StepCardListContainer'

interface RentalStepCardListContainerProps {
  vm: TaxPrepOrganizerViewModel
  testId?: string
}

export const RentalStepCardListContainer = observer(
  function RentalStepCardListContainer({
    vm,
    testId,
  }: RentalStepCardListContainerProps) {
    const title =
      vm.rentalVms.length > 1
        ? `${vm.rentalVms.length} Rental properties`
        : vm.rentalVms.length === 1
          ? '1 Rental property'
          : 'Rental property'

    return (
      <>
        <StepCardListContainer
          title={title}
          actions={
            <ButtonGroup>
              <Button
                label="Add rental property"
                variant="outline"
                onPress={vm.addRentalDialogVm.showDialog}
                isSkeleton={vm.isLoading}
                testId={testId ? `${testId}-add-rental-button` : undefined}
              />
              <Button
                iconLeft="tick"
                label="Confirm"
                isPending={vm.save.pending}
                onPress={() => vm.save()}
                isSkeleton={vm.isLoading}
                testId={testId ? `${testId}-confirm-button` : undefined}
              />
            </ButtonGroup>
          }
          hasError={vm.showValidationError}
          renderEmptyState={() => (
            <ExpandableCardListEmptyState
              title={
                'No rental properties added yet. Click "Add rental property" to get started.'
              }
              testId={testId ? `${testId}-empty-state` : undefined}
            />
          )}
          isSkeleton={vm.isLoading}
          testId={testId}
        >
          {vm.rentalVms.map((rentalVm, index) => (
            <RentalAccordion
              key={rentalVm.id || index}
              vm={rentalVm}
              onRemove={() =>
                vm.removeRentalDialogVm.showDialog({
                  id: Id.assert(rentalVm.id),
                  displayName: rentalVm.displayName,
                })
              }
              testId={
                testId ? `${testId}-rental-accordion-${index}` : undefined
              }
            />
          ))}
        </StepCardListContainer>
        <RemoveRentalDialog
          vm={vm.removeRentalDialogVm}
          testId={testId ? `${testId}-remove-rental-dialog` : undefined}
        />
      </>
    )
  },
)
